// ============================================================
// AdminTelegramPage — M11.A (PRD §6.1.M R-P0-M1..M6)
// ============================================================
// Admin UI cấu hình Telegram Bot + quản lý người nhận thông báo.
// Foundation đã xong ở BE (routes/telegram.js + services/telegramService.js).
//
// 2 tab:
//   - Cấu hình: bot_token (ghi đè, không đọc lại) + enabled + webhook_url + test button
//   - Người nhận: bảng CRUD + 5 checkbox notify (ai_ask, login_fail, budget_alert,
//     weekly_report, milestones)
//
// Bảo mật:
//   - Token không bao giờ trả lại từ BE (chỉ has_token + '•••••••••')
//   - Xoá token cần confirm_clear_token=true
//   - Tất cả route require admin role (adminOnly preHandler ở BE)
// ============================================================

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../lib/auth.js';
import { api } from '../lib/api.js';
import { showConfirm } from '../lib/dialogs.js';

const NOTIFY_FIELDS = [
  { key: 'notify_ai_ask',        label: 'AI Ask',         hint: '2 lần LLM không chắc → gọi admin' },
  { key: 'notify_login_fail',    label: 'Login/L2 lockout', hint: 'NV nhập OTP sai 5 lần → khoá' },
  { key: 'notify_budget_alert',  label: 'Ngân sách',       hint: '80%/100% ngưỡng LLM/embed' },
  { key: 'notify_weekly_report', label: 'Báo cáo tuần',    hint: 'Gửi thứ 2 đầu tuần (P1)' },
  { key: 'notify_milestones',    label: 'Cột mốc',         hint: 'Nội quy mới / milestone khác' },
  { key: 'notify_exam_result',   label: 'Kết quả kiểm tra', hint: 'NV submit bài thi bộ reward (đạt/trượt) — M18' },
];

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });
  } catch { return iso; }
}

export default function AdminTelegramPage() {
  const nav = useNavigate();
  const { user } = useAuthStore();
  // M146: manager với quyền 'telegram' cũng truy cập được
  const isAdmin = (function () {
    if (user?.role === 'admin') return true;
    if (user?.role === 'manager' && Array.isArray(user?.permissions)) return user.permissions.includes('telegram');
    return false;
  })();

  const [tab, setTab] = useState('config'); // 'config' | 'recipients' | 'infra'

  // ----- Config state -----
  const [cfgLoading, setCfgLoading] = useState(false);
  const [cfg, setCfg] = useState(null); // {enabled, has_token, token_preview, webhook_url, updated_at, is_operational}
  const [cfgDirty, setCfgDirty] = useState({ bot_token: '', enabled: false, webhook_url: '' });
  const [testChatId, setTestChatId] = useState('');
  const [testing, setTesting] = useState(false);

  // ----- Recipients state -----
  const [recips, setRecips] = useState([]);
  const [rLoading, setRLoading] = useState(false);
  const [editing, setEditing] = useState(null); // {id?, chat_id, label, user_id, notify_*}

  // ----- M319 Infra monitoring state -----
  const [infra, setInfra] = useState(null); // {enabled, last_check_at, last_alert_at, last_backup_age_hours, threshold_hours, audit_summary}
  const [infraLoading, setInfraLoading] = useState(false);
  const [infraSaving, setInfraSaving] = useState(false);

  // ----- Access guard -----
  useEffect(() => {
    if (!isAdmin) {
      toast.error('Chỉ admin mới truy cập được');
      nav('/', { replace: true });
    }
  }, [isAdmin, nav]);

  // ----- Initial load -----
  useEffect(() => {
    if (isAdmin) {
      refreshConfig();
      refreshRecipients();
      refreshInfra();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  async function refreshInfra() {
    setInfraLoading(true);
    try {
      const res = await api.telegramInfraMonitoringGet();
      setInfra(res);
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Không tải được trạng thái Infra monitoring');
    } finally {
      setInfraLoading(false);
    }
  }

  async function toggleInfra(nextEnabled) {
    setInfraSaving(true);
    try {
      await api.telegramInfraMonitoringToggle(nextEnabled);
      toast.success(nextEnabled ? 'Đã bật cảnh báo backup' : 'Đã tắt cảnh báo backup');
      await refreshInfra();
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Không lưu được');
    } finally {
      setInfraSaving(false);
    }
  }

  async function refreshConfig() {
    setCfgLoading(true);
    try {
      const res = await api.telegramConfigGet();
      setCfg(res);
      setCfgDirty({
        bot_token: '',
        enabled: !!res.enabled,
        webhook_url: res.webhook_url || '',
      });
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Không tải được config');
    } finally {
      setCfgLoading(false);
    }
  }

  async function saveConfig() {
    const body = {};
    // Chỉ gửi bot_token khi admin nhập (tránh ghi đè rỗng)
    const token = (cfgDirty.bot_token || '').trim();
    if (token) {
      if (!/^\d{5,}:[A-Za-z0-9_-]{20,}$/.test(token)) {
        toast.error('bot_token không đúng định dạng Telegram (dạng "12345:abc...")');
        return;
      }
      body.bot_token = token;
    }
    // enabled + webhook_url luôn gửi
    body.enabled = !!cfgDirty.enabled;
    body.webhook_url = (cfgDirty.webhook_url || '').trim() || null;

    try {
      await api.telegramConfigUpdate(body);
      toast.success('Đã lưu cấu hình');
      await refreshConfig();
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Lưu thất bại');
    }
  }

  async function clearToken() {
    const ok = await showConfirm({
      title: 'Xoá bot token',
      message: 'Xoá bot_token khỏi hệ thống?\nTelegram sẽ NGỪNG gửi tin cho đến khi nhập token mới.',
      confirmText: 'Xoá token',
      dangerous: true,
    });
    if (!ok) return;
    try {
      await api.telegramConfigUpdate({ bot_token: null, confirm_clear_token: true });
      toast.success('Đã xoá token');
      await refreshConfig();
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Xoá token thất bại');
    }
  }

  async function sendTest() {
    const chatId = (testChatId || '').trim();
    if (!chatId) { toast.error('Nhập chat_id để test'); return; }
    setTesting(true);
    try {
      const t = toast.loading('Đang gửi test...');
      const res = await api.telegramConfigTest(chatId);
      toast.dismiss(t);
      toast.success(`✓ Đã gửi test tới ${chatId}${res.message_id ? ` · msg #${res.message_id}` : ''}`, { duration: 4000 });
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Test thất bại');
    } finally {
      setTesting(false);
    }
  }

  // ----- Recipients -----
  async function refreshRecipients() {
    setRLoading(true);
    try {
      const res = await api.telegramRecipientsList();
      setRecips(res.recipients || []);
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Không tải được recipients');
    } finally {
      setRLoading(false);
    }
  }

  function openCreateRecipient() {
    setEditing({
      id: null,
      chat_id: '',
      label: '',
      user_id: '',
      notify_ai_ask: true,
      notify_login_fail: true,
      notify_budget_alert: true,
      notify_weekly_report: true,
      notify_milestones: true,
      notify_exam_result: true, // M18
    });
  }

  function openEditRecipient(row) {
    setEditing({
      id: row.id,
      chat_id: row.chat_id,
      label: row.label || '',
      user_id: row.user_id || '',
      notify_ai_ask: !!row.notify_ai_ask,
      notify_login_fail: !!row.notify_login_fail,
      notify_budget_alert: !!row.notify_budget_alert,
      notify_weekly_report: !!row.notify_weekly_report,
      notify_milestones: !!row.notify_milestones,
      notify_exam_result: row.notify_exam_result !== undefined ? !!row.notify_exam_result : true, // M18 — default true nếu DB chưa migrate
    });
  }

  async function saveRecipient() {
    if (!editing) return;
    const chatId = (editing.chat_id || '').trim();
    if (!chatId) { toast.error('chat_id không được để trống'); return; }
    const body = {
      chat_id: chatId,
      label: (editing.label || '').trim() || null,
      user_id: editing.user_id ? Number(editing.user_id) : null,
      notify_ai_ask: !!editing.notify_ai_ask,
      notify_login_fail: !!editing.notify_login_fail,
      notify_budget_alert: !!editing.notify_budget_alert,
      notify_weekly_report: !!editing.notify_weekly_report,
      notify_milestones: !!editing.notify_milestones,
      notify_exam_result: !!editing.notify_exam_result, // M18
    };
    try {
      if (editing.id) {
        await api.telegramRecipientUpdate(editing.id, body);
        toast.success('Đã cập nhật recipient');
      } else {
        await api.telegramRecipientCreate(body);
        toast.success('Đã thêm recipient');
      }
      setEditing(null);
      await refreshRecipients();
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Lưu thất bại');
    }
  }

  async function delRecipient(id) {
    const ok = await showConfirm({
      title: 'Xoá recipient',
      message: 'Xoá recipient này?',
      confirmText: 'Xoá',
      dangerous: true,
    });
    if (!ok) return;
    try {
      await api.telegramRecipientDelete(id);
      toast.success('Đã xoá');
      await refreshRecipients();
      if (editing?.id === id) setEditing(null);
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Xoá thất bại');
    }
  }

  async function toggleRecipientFlag(row, key, next) {
    try {
      await api.telegramRecipientUpdate(row.id, { [key]: next });
      await refreshRecipients();
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Cập nhật thất bại');
    }
  }

  // ----- Render -----
  return (
    <div className="min-h-full p-4">
      <div className="max-w-6xl mx-auto">
        {/* HEADER */}
        <header className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="text-sm text-slate-500 hover:text-slate-700"
              data-testid="telegram-back"
            >← Trang admin</Link>
            <div>
              <h1 className="text-xl font-semibold text-brand-700">Telegram Bot</h1>
              <p className="text-xs text-slate-500">
                Cấu hình bot + người nhận. Cảnh báo AI Ask / Login fail / Budget / Nội quy mới / Exam fail gửi tự động qua Telegram.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`text-xs px-2 py-1 rounded-full border ${
                cfg?.is_operational
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}
              data-testid="telegram-operational"
            >
              {cfg?.is_operational ? '🟢 Đang hoạt động' : '🟡 Chưa hoạt động'}
            </span>
          </div>
        </header>

        {/* TAB NAV */}
        <div className="flex gap-2 mb-4 border-b border-slate-200">
          <button
            type="button"
            onClick={() => setTab('config')}
            data-testid="telegram-tab-config"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === 'config'
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >⚙️ Cấu hình Bot</button>
          <button
            type="button"
            onClick={() => setTab('recipients')}
            data-testid="telegram-tab-recipients"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === 'recipients'
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >👥 Người nhận ({recips.length})</button>
          <button
            type="button"
            onClick={() => setTab('infra')}
            data-testid="telegram-tab-infra"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === 'infra'
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >🛡️ Hạ tầng</button>
        </div>

        {/* TAB: CONFIG */}
        {tab === 'config' && (
          <div className="space-y-4">
            {cfgLoading && !cfg && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-10 text-center text-sm text-slate-400">
                Đang tải…
              </div>
            )}
            {cfg && (
              <>
                {/* Bot Token */}
                <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-slate-800">🔑 Bot Token</h2>
                    {cfg.has_token ? (
                      <span className="text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded">
                        ✓ Đã có token ({cfg.token_preview})
                      </span>
                    ) : (
                      <span className="text-[11px] bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded">
                        ⚠ Chưa có token
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    Lấy token từ <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">@BotFather</a> trên Telegram. Token được mã hoá AES-256-GCM trước khi lưu DB, <b>không đọc lại được</b>.
                  </p>
                  <input
                    type="password"
                    value={cfgDirty.bot_token}
                    onChange={e => setCfgDirty({ ...cfgDirty, bot_token: e.target.value })}
                    placeholder={cfg.has_token ? 'Để trống = giữ token hiện tại. Nhập để GHI ĐÈ.' : 'Ví dụ: 123456789:AAEhBP0av_IbXyZ...'}
                    className="w-full rounded-lg border border-slate-300 focus:border-brand-500 focus:ring focus:ring-brand-200 focus:outline-none p-2 text-sm font-mono"
                    data-testid="telegram-token-input"
                    autoComplete="off"
                  />
                  {cfg.has_token && (
                    <button
                      type="button"
                      onClick={clearToken}
                      className="mt-2 text-xs px-2 py-1 rounded bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200"
                      data-testid="telegram-clear-token"
                    >🗑 Xoá token</button>
                  )}
                </section>

                {/* Enable + Webhook */}
                <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 space-y-3">
                  <h2 className="text-sm font-semibold text-slate-800 mb-2">⚙️ Trạng thái</h2>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!cfgDirty.enabled}
                      onChange={e => setCfgDirty({ ...cfgDirty, enabled: e.target.checked })}
                      className="accent-brand-600 mt-0.5"
                      data-testid="telegram-enabled"
                    />
                    <div>
                      <div className="text-sm font-medium text-slate-800">Bật Telegram</div>
                      <div className="text-[11px] text-slate-500">
                        Tắt = không gửi bất kỳ tin nào. Cần bật + có token hợp lệ + ≥ 1 recipient mới gửi được.
                      </div>
                    </div>
                  </label>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Webhook URL <span className="text-slate-400 font-normal">(tuỳ chọn, cho inbound messages từ bot)</span>
                    </label>
                    <input
                      type="url"
                      value={cfgDirty.webhook_url}
                      onChange={e => setCfgDirty({ ...cfgDirty, webhook_url: e.target.value })}
                      placeholder="https://yourdomain.com/api/telegram/webhook"
                      className="w-full rounded-lg border border-slate-300 focus:border-brand-500 focus:ring focus:ring-brand-200 focus:outline-none p-2 text-sm"
                      data-testid="telegram-webhook"
                    />
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <button
                      type="button"
                      onClick={saveConfig}
                      className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold shadow-sm"
                      data-testid="telegram-save"
                    >💾 Lưu cấu hình</button>
                    <button
                      type="button"
                      onClick={refreshConfig}
                      className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm"
                      data-testid="telegram-reload"
                    >↻ Tải lại</button>
                    <span className="text-[11px] text-slate-400 ml-auto">
                      Cập nhật lần cuối: {fmtTime(cfg.updated_at) || '—'}
                    </span>
                  </div>
                </section>

                {/* Test send */}
                <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
                  <h2 className="text-sm font-semibold text-slate-800 mb-2">🧪 Test gửi tin</h2>
                  <p className="text-xs text-slate-500 mb-2">
                    Gửi 1 tin test tới chat_id bất kỳ để verify bot token + network hoạt động. Muốn biết chat_id của bạn, nhắn bất kỳ tin nào cho <a href="https://t.me/userinfobot" target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">@userinfobot</a>.
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={testChatId}
                      onChange={e => setTestChatId(e.target.value)}
                      placeholder="chat_id (ví dụ: 123456789)"
                      className="flex-1 rounded-lg border border-slate-300 focus:border-brand-500 focus:ring focus:ring-brand-200 focus:outline-none p-2 text-sm font-mono"
                      data-testid="telegram-test-chat-id"
                    />
                    <button
                      type="button"
                      onClick={sendTest}
                      disabled={testing || !cfg.is_operational}
                      className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white text-sm font-semibold"
                      data-testid="telegram-test-send"
                    >{testing ? 'Đang gửi…' : '📨 Gửi test'}</button>
                  </div>
                  {!cfg.is_operational && (
                    <div className="text-[11px] text-amber-600 mt-2">
                      ⚠ Bot chưa hoạt động (cần bật + token hợp lệ). Lưu cấu hình trước.
                    </div>
                  )}
                </section>
              </>
            )}
          </div>
        )}

        {/* TAB: RECIPIENTS */}
        {tab === 'recipients' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-500">
                Mỗi recipient = 1 chat_id (user hoặc group). Checkbox quyết định loại notify nào sẽ gửi tới chat này.
              </div>
              <button
                type="button"
                onClick={openCreateRecipient}
                className="px-3 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold shadow-sm"
                data-testid="telegram-new-recipient"
              >➕ Thêm người nhận</button>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              {rLoading && <div className="p-6 text-center text-sm text-slate-400">Đang tải…</div>}
              {!rLoading && recips.length === 0 && (
                <div className="p-10 text-center text-sm text-slate-400">
                  Chưa có người nhận nào. Bấm "➕ Thêm người nhận" để bắt đầu.
                </div>
              )}
              {!rLoading && recips.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-600">
                      <tr>
                        <th className="text-left py-2 px-3 font-semibold">chat_id</th>
                        <th className="text-left py-2 px-3 font-semibold">Label</th>
                        {NOTIFY_FIELDS.map(f => (
                          <th key={f.key} className="text-center py-2 px-2 font-semibold" title={f.hint}>
                            {f.label}
                          </th>
                        ))}
                        <th className="text-right py-2 px-3 font-semibold">Thao tác</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recips.map(row => (
                        <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`telegram-recipient-row-${row.id}`}>
                          <td className="py-2 px-3 font-mono text-xs text-slate-700">{row.chat_id}</td>
                          <td className="py-2 px-3 text-slate-600">
                            {row.label || <span className="text-slate-300 italic">—</span>}
                            {row.user_id && <div className="text-[10px] text-slate-400">user#{row.user_id}</div>}
                          </td>
                          {NOTIFY_FIELDS.map(f => (
                            <td key={f.key} className="py-2 px-2 text-center">
                              <input
                                type="checkbox"
                                checked={!!row[f.key]}
                                onChange={e => toggleRecipientFlag(row, f.key, e.target.checked)}
                                className="accent-brand-600 cursor-pointer"
                                data-testid={`telegram-${row.id}-${f.key}`}
                              />
                            </td>
                          ))}
                          <td className="py-2 px-3 text-right whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => openEditRecipient(row)}
                              className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
                              data-testid={`telegram-edit-${row.id}`}
                            >✏️ Sửa</button>
                            <button
                              type="button"
                              onClick={() => delRecipient(row.id)}
                              className="text-xs px-2 py-1 rounded bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 ml-1"
                              data-testid={`telegram-delete-${row.id}`}
                            >🗑</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB: INFRA MONITORING (M319 2026-05-29) */}
        {tab === 'infra' && (
          <div className="space-y-4">
            {infraLoading && !infra && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-10 text-center text-sm text-slate-400">
                Đang tải…
              </div>
            )}
            {infra && (
              <>
                {/* Toggle + status */}
                <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
                  <div className="flex items-start justify-between mb-3 gap-4">
                    <div>
                      <h2 className="text-sm font-semibold text-slate-800">🛡️ Cảnh báo backup hỏng</h2>
                      <p className="text-xs text-slate-500 mt-1 max-w-xl">
                        Mỗi sáng 8h, hệ thống kiểm tra file backup database mới nhất. Nếu file mới nhất quá <b>{infra.threshold_hours} giờ</b> (~2 ngày), Telegram sẽ gửi cảnh báo tới những người có flag <b>Ngân sách</b>. Phòng tránh sự cố backup gãy âm thầm như đã xảy ra tháng 5 (M307).
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleInfra(!infra.enabled)}
                      disabled={infraSaving}
                      data-testid="telegram-infra-toggle"
                      className={`shrink-0 px-4 py-2 rounded-lg text-sm font-medium border transition ${
                        infra.enabled
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                          : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                      } disabled:opacity-50`}
                    >
                      {infra.enabled ? '🟢 Đang BẬT' : '⚪ Đang TẮT'}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <div className="text-[11px] text-slate-500 uppercase tracking-wide">Lần check gần nhất</div>
                      <div className="text-sm font-medium text-slate-800 mt-1">
                        {infra.last_check_at ? fmtTime(infra.last_check_at) : '(chưa chạy lần nào)'}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <div className="text-[11px] text-slate-500 uppercase tracking-wide">Tuổi file backup mới nhất</div>
                      <div className={`text-sm font-medium mt-1 ${
                        infra.last_backup_age_hours === null
                          ? 'text-slate-400'
                          : infra.last_backup_age_hours > infra.threshold_hours
                            ? 'text-rose-700'
                            : 'text-emerald-700'
                      }`}>
                        {infra.last_backup_age_hours === null
                          ? '—'
                          : `${infra.last_backup_age_hours.toFixed(1)} giờ ${
                              infra.last_backup_age_hours > infra.threshold_hours ? '⚠️' : '✓'
                            }`}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <div className="text-[11px] text-slate-500 uppercase tracking-wide">Lần gửi cảnh báo gần nhất</div>
                      <div className="text-sm font-medium text-slate-800 mt-1">
                        {infra.last_alert_at ? fmtTime(infra.last_alert_at) : '(chưa gửi lần nào)'}
                      </div>
                    </div>
                  </div>
                </section>

                {/* Audit summary — "Em đã làm gì" */}
                {infra.audit_summary && (
                  <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
                    <div className="mb-3">
                      <h2 className="text-sm font-semibold text-slate-800">📋 {infra.audit_summary.title}</h2>
                      <p className="text-xs text-slate-500 mt-1">
                        Ngày hoàn thành: {infra.audit_summary.completed_at}
                      </p>
                    </div>
                    <div className="space-y-3">
                      {(infra.audit_summary.changes || []).map((c, idx) => (
                        <div key={idx} className="border-l-4 border-brand-300 pl-3 py-1">
                          <div className="text-xs font-mono text-slate-500">{c.file}</div>
                          <div className="text-sm text-slate-800 mt-1">{c.change}</div>
                          {c.impact && (
                            <div className="text-xs text-emerald-700 mt-1">
                              → <i>{c.impact}</i>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* EDIT RECIPIENT MODAL */}
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
          onClick={() => setEditing(null)}
          data-testid="telegram-edit-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-slate-800 mb-4">
              {editing.id ? `✏️ Sửa người nhận #${editing.id}` : '➕ Thêm người nhận mới'}
            </h2>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  chat_id <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={editing.chat_id}
                  onChange={e => setEditing({ ...editing, chat_id: e.target.value })}
                  disabled={!!editing.id}
                  placeholder="Ví dụ: 123456789 (cá nhân) hoặc -100123456789 (group)"
                  className="w-full rounded-lg border border-slate-300 focus:border-brand-500 focus:ring focus:ring-brand-200 focus:outline-none p-2 text-sm font-mono disabled:bg-slate-100 disabled:text-slate-500"
                  data-testid="telegram-edit-chat-id"
                />
                {editing.id && (
                  <div className="text-[11px] text-slate-400 mt-1">chat_id không đổi được sau khi tạo. Xoá + thêm mới nếu cần.</div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Label <span className="text-slate-400 font-normal">(tên gợi nhớ, ví dụ "Admin Ngọc")</span>
                </label>
                <input
                  type="text"
                  value={editing.label}
                  onChange={e => setEditing({ ...editing, label: e.target.value })}
                  maxLength={200}
                  placeholder="Tên để phân biệt giữa các recipient"
                  className="w-full rounded-lg border border-slate-300 focus:border-brand-500 focus:ring focus:ring-brand-200 focus:outline-none p-2 text-sm"
                  data-testid="telegram-edit-label"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  user_id <span className="text-slate-400 font-normal">(FK tới bảng users, tuỳ chọn)</span>
                </label>
                <input
                  type="number"
                  value={editing.user_id}
                  onChange={e => setEditing({ ...editing, user_id: e.target.value })}
                  placeholder="Để trống nếu recipient không gắn với user nào"
                  className="w-full rounded-lg border border-slate-300 focus:border-brand-500 focus:ring focus:ring-brand-200 focus:outline-none p-2 text-sm"
                  data-testid="telegram-edit-user-id"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">Loại thông báo</label>
                <div className="space-y-2">
                  {NOTIFY_FIELDS.map(f => (
                    <label key={f.key} className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={!!editing[f.key]}
                        onChange={e => setEditing({ ...editing, [f.key]: e.target.checked })}
                        className="accent-brand-600 mt-0.5"
                        data-testid={`telegram-edit-${f.key}`}
                      />
                      <div>
                        <div className="text-sm font-medium text-slate-800">{f.label}</div>
                        <div className="text-[11px] text-slate-500">{f.hint}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium"
                data-testid="telegram-cancel"
              >Huỷ</button>
              <button
                onClick={saveRecipient}
                className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold"
                data-testid="telegram-save-recipient"
              >{editing.id ? 'Lưu' : 'Tạo mới'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
